// inkOnPlate — the ink for a plate whose HUE comes from data:
// `color: (el, s) => el.call('inkOnPlate', s.color || 'blue')`.
//
// A fixed plate has a fixed, measured ink (`fillInk` on the brand hues — see
// design-direction.md §6). A plate painted from data (`s.color`: an agent's or
// a person's colour) cannot: white holds on purple (6.9:1) and fails on blue
// (3.62), orange (2.9) and green (2.81); near-black fixes those and fails on
// purple (3.0). So the ink is CHOSEN per plate: whichever of the brand's two
// plate inks — `fillInk` (near-black) or `white` — reads with more contrast.
//
// The colour is read off the RUNNING design system
// (`context.designSystem.color.<token>.rgb`, e.g. '0, 133, 255'), so a project
// that re-tones a hue gets the right ink with no table to keep in step. A shade
// or alpha modifier (`blue.85`, `purple+20`) reads its base hue. A plate that
// cannot be measured (an unknown token, a scheme PAIR, no design system) keeps
// `white`, the ink these tiles always had.
//
// Self-contained on purpose: an exported function is serialized alone for
// platform delivery, so it may not lean on a module-scope helper.
export function inkOnPlate(token) {
  const name = String(token || '').split(/[.+=-]/)[0]
  const palette =
    this && this.context && this.context.designSystem && this.context.designSystem.color
  const entry = palette && name ? palette[name] : null
  const rgb =
    entry && typeof entry.rgb === 'string' ? entry.rgb.split(',').map((part) => Number(part)) : null
  if (!rgb || rgb.length < 3 || rgb.some((value) => Number.isNaN(value))) return 'white'
  const lin = (channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  const lum = (c) => 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2])
  const plate = lum(rgb)
  const onWhite = (1 + 0.05) / (plate + 0.05)
  const onNearBlack = (plate + 0.05) / (lum([4, 4, 4]) + 0.05)
  return onNearBlack >= onWhite ? 'fillInk' : 'white'
}
